import React, {useMemo, useState} from 'react';
import DashboardLayout from "../../../layouts/DashboardLayout";
import {useTranslation} from "react-i18next";
import ButtonInternalLink from "../../../components/ButtonInternalLink";
import {useQuery} from "react-query";
import SortIcon from "../../../components/SortIcon";
import {apollo} from "../../../utils/apollo";
import {GET_SUB_USER_LIST} from "../../../utils/graphql";
import PaginationFull from "../../../components/PaginationFull";
import {UsersIcon, PlusIcon} from '@heroicons/react/24/outline';
import Loading from "../../../components/Loading";
import Button from "../../../components/Button";

function SubUser() {
    const {t} = useTranslation();
    const [sort, setSort] = useState({field: 'id', isAscending: false});
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('')
    const tableHeader = [
        {column: 'user_uuid', title: t('user_management.table.user_uuid'), sortable: true},
        {column: 'name', title: t('user_management.table.name'), sortable: true},
        // {column: 'role', title: t('user_management.table.role'), sortable: false},
        {column: 'email', title: t('user_management.table.email'), sortable: true},
        {column: 'country', title: t('user_management.table.country'), sortable: false},
        {column: 'verified', title: t('user_management.table.verified'), sortable: true},
        {column: 'active', title: t('user_management.table.active'), sortable: true},
        {column: 'created_at', title: t('user_management.table.date'), sortable: true},
        {column: 'actions', title: '', sortable: false},
    ]

    const fetchUsersList = useQuery(
        ['users_list', {page, sort, statusFilter}],
        async () => {
            const {data} = await apollo.query({
                query: GET_SUB_USER_LIST,
                variables: {
                    sortBy: sort.field?.toUpperCase(),
                    sortDirection: sort.isAscending ? "ASC" : "DESC",
                    page,
                    first: 15,
                    filter: {
                        status: statusFilter
                    }
                },
            });
            return data?.getSubUserList;
        },
        {keepPreviousData: false}
    );
    const records = useMemo(() => fetchUsersList?.isSuccess ? fetchUsersList?.data : null, [fetchUsersList]);

    const handleChangeSort = (field) => {
        if (sort.field === field) {
            setSort(prevState => ({...prevState, isAscending: !prevState.isAscending}));
        } else {
            setSort({field: field, isAscending: true});
        }
    };

    return (
        <DashboardLayout>
            <div className="mb-10 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                <div className="flex items-center gap-4">
                    <div className="p-3 rounded-2xl bg-white shadow-sm border border-gray-100 text-[#0b6a8c]">
                        <UsersIcon className="h-7 w-7"/>
                    </div>
                    <div>
                        <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">{t('user_management.page_title')}</h2>
                        <p className="text-gray-500 font-medium">{t('user_management.page_description')}</p>
                    </div>
                </div>
                <ButtonInternalLink
                    href="/merchant/sub-users/create"
                    className="rounded-2xl px-6 py-3 shadow-lg shadow-blue-600/10 flex items-center gap-2"
                >
                    <PlusIcon className="h-5 w-5" />
                    {t('user_management.create_sub_user')}
                </ButtonInternalLink>
            </div>

            {fetchUsersList.isLoading && (
                <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 p-20 text-center">
                    <Loading message={t('_.loading')} fullHeight={false} />
                </div>
            )}

            {(fetchUsersList.isSuccess && (!records || !records?.data?.length)) && (
                <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 p-20 text-center">
                    <div className="mx-auto h-20 w-20 rounded-full bg-gray-50 flex items-center justify-center border border-gray-100 shadow-inner text-gray-400">
                        <UsersIcon className="h-10 w-10" />
                    </div>
                    <h3 className="mt-8 text-xl font-bold text-gray-900">{t('_.no_data')}</h3>
                    <div className="mt-8">
                        <Button
                            onClick={() => fetchUsersList.refetch()}
                            className="rounded-2xl px-8"
                        >
                            {t('_.refresh')}
                        </Button>
                    </div>
                </div>
            )}

            {(fetchUsersList.isSuccess && records && records?.data?.length > 0) && <>
                <div className="bg-white/40 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-8 py-5 border-b border-gray-100/50">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 rounded-2xl bg-cyan-50 text-[#0b6a8c]">
                                    <UsersIcon className="h-5 w-5" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">{t('user_management.page_title')}</h3>
                            </div>
                            <span className="text-sm font-bold text-[#0b6a8c] bg-cyan-50 px-4 py-1.5 rounded-full">
                                {records?.paginatorInfo?.total || records?.data?.length || 0} {t('_.records')}
                            </span>
                        </div>
                    </div>
                    <div className="overflow-x-auto p-4">
                        <table className="min-w-full border-separate border-spacing-y-3">
                            <thead>
                            <tr>
                                {tableHeader?.map(item => (<th key={item.column} scope="col"
                                                               className="px-6 py-2 ltr:text-left rtl:text-right text-xs font-bold text-gray-400 uppercase tracking-widest">
                                    {!item.hidden && <>
                                        {item.sortable ?
                                            <button onClick={() => handleChangeSort(item.column)} type="button"
                                                    className="group inline-flex items-center hover:text-[#0b6a8c] transition-colors">
                                                {item.title}
                                                <SortIcon sort={sort} field={item.column}/>
                                            </button> : <span>{item.title}</span>}
                                    </>}
                                </th>))}
                            </tr>
                            </thead>
                            <tbody>
                            {records?.data?.map((record) => (
                                <tr key={record?.userUuid} className="group transition-all duration-300">
                                    <td className="px-6 py-4 whitespace-nowrap bg-white/80 group-hover:bg-white rounded-l-3xl transition-all border-y border-l border-gray-50 group-hover:border-cyan-50">
                                        <div className="text-sm font-mono font-bold text-gray-900">{record?.userUuid}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
                                        <div className="text-sm font-bold text-gray-900">{record?.name}</div>
                                    </td>
                                    {/*<td className="px-6 py-4 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">*/}
                                    {/*    <div className="text-sm font-medium text-gray-600">{record?.roles?.join(', ')}</div>*/}
                                    {/*</td>*/}
                                    <td className="px-6 py-4 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
                                        <div className="text-sm text-gray-500 font-medium">{record?.email}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
                                        <div className="text-sm text-gray-500 font-medium">{record?.country?.englishName}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
                                        {record?.verified ? (
                                            <div className="px-3 py-1 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100 inline-block uppercase tracking-wider">
                                                {t('_.yes')}
                                            </div>
                                        ) : (
                                            <div className="px-3 py-1 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-100 inline-block uppercase tracking-wider">
                                                {t('_.no')}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
                                        {record?.active ? (
                                            <div className="px-3 py-1 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100 inline-block uppercase tracking-wider">
                                                {t('_.active')}
                                            </div>
                                        ) : (
                                            <div className="px-3 py-1 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-100 inline-block uppercase tracking-wider">
                                                {t('_.inactive')}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
                                        <div className="text-sm text-gray-500 font-medium">{record?.createdAt}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap bg-white/80 group-hover:bg-white rounded-r-3xl transition-all border-y border-r border-gray-50 group-hover:border-cyan-50 text-right">
                                        <ButtonInternalLink
                                            className="!rounded-xl !px-4 !py-1.5 text-xs font-bold shadow-sm"
                                            href={`/merchant/sub-users/view/${record?.userUuid}`}>
                                            {t('_.view')}
                                        </ButtonInternalLink>
                                    </td>
                                </tr>))}
                            </tbody>
                        </table>
                    </div>
                    <div className="px-8 py-6 border-t border-gray-100/50 bg-gray-50/30">
                        <PaginationFull paginator={records?.paginatorInfo} get={page} set={setPage}/>
                    </div>
                </div>
            </>}
        </DashboardLayout>
    );
}

export default SubUser;

